// Blog data layer — Firestore-backed reads (public-friendly) + admin-only
// writes. Posts live in `blogPosts/{slug}`; the slug is the doc ID, so URL
// integrity is enforced automatically (no two posts can share a slug).
//
// Status model:
//   - draft     → admin-only readable, not on /blog
//   - published → public-readable, listed on /blog index
//   - archived  → kept on the doc for history, not listed; canonical URL
//                 returns 404 (UI returns null + nav to /blog).

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  Timestamp,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { db } from './firebase';

export type BlogStatus = 'draft' | 'published' | 'archived';

export interface BlogPost {
  slug: string;
  title: string;
  subtitle?: string;
  // Markdown source. Rendered to HTML at view time.
  content: string;
  excerpt: string;
  coverImageUrl?: string;
  author: string;
  authorUid: string;
  status: BlogStatus;
  tags: string[];
  publishedAt?: number;     // ms epoch — set when first published
  updatedAt: number;
  createdAt: number;
  // Optional explicit SEO overrides; otherwise we fall back to title + excerpt.
  seoTitle?: string;
  seoDescription?: string;
}

function fromDoc(d: DocumentData & { id: string }): BlogPost {
  const data = (d as DocumentData) as Partial<BlogPost> & Record<string, unknown>;
  return {
    slug: d.id,
    title: (data.title as string) ?? 'Untitled',
    subtitle: data.subtitle as string | undefined,
    content: (data.content as string) ?? '',
    excerpt: (data.excerpt as string) ?? '',
    coverImageUrl: data.coverImageUrl as string | undefined,
    author: (data.author as string) ?? 'CNA Prep Pro Team',
    authorUid: (data.authorUid as string) ?? '',
    status: (data.status as BlogStatus) ?? 'draft',
    tags: (data.tags as string[] | undefined) ?? [],
    publishedAt: typeof data.publishedAt === 'number' ? (data.publishedAt as number) : undefined,
    updatedAt: (data.updatedAt as number) ?? 0,
    createdAt: (data.createdAt as number) ?? 0,
    seoTitle: data.seoTitle as string | undefined,
    seoDescription: data.seoDescription as string | undefined,
  };
}

// Derive a URL-safe slug from a title. Idempotent on already-slugged input.
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

// Cheap excerpt: first 160 chars of plaintext-ish content.
export function excerptFrom(markdown: string): string {
  const stripped = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[#*_`>\-]/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]\([^)]*\)/g, (m) => m.replace(/\[([^\]]*)\][^)]*$/, '$1'))
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.length <= 200 ? stripped : stripped.slice(0, 197).trimEnd() + '…';
}

export async function listPublishedPosts(): Promise<BlogPost[]> {
  // Bumped to 500 — covers all foreseeable post volume; the client paginates
  // the result. If we ever exceed this, switch to startAfter cursors.
  const q = query(
    collection(db, 'blogPosts'),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(500),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromDoc({ id: d.id, ...d.data() }));
}

export async function listAllPostsForAdmin(): Promise<BlogPost[]> {
  const q = query(
    collection(db, 'blogPosts'),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromDoc({ id: d.id, ...d.data() }));
}

export async function getPost(slug: string): Promise<BlogPost | null> {
  const snap = await getDoc(doc(db, 'blogPosts', slug));
  if (!snap.exists()) return null;
  return fromDoc({ id: snap.id, ...snap.data() });
}

// Upsert from the admin editor. The slug field is the doc ID — pass an
// existing slug to update in place; otherwise the caller should generate a
// fresh slug via `slugify(title)` before calling this.
export interface UpsertInput {
  slug: string;
  title: string;
  subtitle?: string;
  content: string;
  coverImageUrl?: string;
  author: string;
  authorUid: string;
  status: BlogStatus;
  tags: string[];
  seoTitle?: string;
  seoDescription?: string;
}

export async function upsertPost(input: UpsertInput): Promise<void> {
  const now = Timestamp.now().toMillis();
  const ref = doc(db, 'blogPosts', input.slug);
  const existing = await getDoc(ref);
  const wasPublished = existing.exists() && (existing.data() as { status?: string }).status === 'published';
  const becomingPublished = input.status === 'published';

  const patch: Record<string, unknown> = {
    title: input.title,
    subtitle: input.subtitle ?? null,
    content: input.content,
    excerpt: excerptFrom(input.content),
    coverImageUrl: input.coverImageUrl ?? null,
    author: input.author,
    authorUid: input.authorUid,
    status: input.status,
    tags: input.tags,
    seoTitle: input.seoTitle ?? null,
    seoDescription: input.seoDescription ?? null,
    updatedAt: now,
  };
  if (!existing.exists()) patch.createdAt = now;
  // Stamp publishedAt the first time we see a doc transition into
  // 'published' OR if a published doc somehow lacks the field (defensive
  // for posts imported via scripts that bypass this helper).
  const existingPublishedAt = existing.exists() ? (existing.data() as { publishedAt?: number }).publishedAt : undefined;
  if (becomingPublished && (!wasPublished || typeof existingPublishedAt !== 'number')) {
    patch.publishedAt = now;
  }

  await setDoc(ref, patch, { merge: true });
}
